import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BookOpen, Clock, CheckCircle, AlertTriangle, PackageX, Loader2 } from 'lucide-react';
import { Badge } from './ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * iter205 · Timeline de préstamos de biblioteca para una matriculada.
 * Se usa dentro del modal de "Datos del Trabajador/a" en AdminDashboard.
 * Agrupa reservas + activos + devueltos + vencidos + expirados ordenados
 * cronológicamente desc.
 */
const ESTADO_META = {
  reservado:  { color: 'bg-amber-100 text-amber-800 border-amber-300',    icon: Clock,         label: 'Reservado' },
  activo:     { color: 'bg-blue-100 text-blue-800 border-blue-300',       icon: BookOpen,      label: 'En préstamo' },
  devuelto:   { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle, label: 'Devuelto' },
  vencido:    { color: 'bg-red-100 text-red-800 border-red-300',          icon: AlertTriangle, label: 'Vencido' },
  expirado:   { color: 'bg-gray-100 text-gray-600 border-gray-300',       icon: PackageX,      label: 'Reserva expirada' },
};

function formatearFecha(f) {
  if (!f) return '-';
  try { return new Date(f).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return f; }
}

function fechaRelevante(p) {
  // Para ordenar: la fecha más representativa del evento más reciente.
  return p.fecha_devolucion || p.fecha_vencimiento || p.fecha_prestamo || p.fecha_reserva || p.created_at;
}

export default function HistorialBibliotecaMatriculada({ numeroMatricula }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!numeroMatricula) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API}/biblioteca/admin/prestamos`, {
          params: { numero_matricula: String(numeroMatricula), limit: 200 },
        });
        if (cancelled) return;
        const sorted = [...(r.data.items || [])].sort((a, b) =>
          new Date(fechaRelevante(b)).getTime() - new Date(fechaRelevante(a)).getTime()
        );
        setItems(sorted);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'No pude cargar el historial');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [numeroMatricula]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-6" data-testid="historial-biblio-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando historial de biblioteca...
      </div>
    );
  }

  if (error) {
    return <div className="text-sm text-red-600 py-3" data-testid="historial-biblio-error">{error}</div>;
  }

  if (items.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic py-4" data-testid="historial-biblio-vacio">
        Esta matriculada no tiene préstamos ni reservas en la Biblioteca "Laura Iglesias".
      </p>
    );
  }

  // Agrupar por año para dar sensación de timeline
  const porAnio = items.reduce((acc, p) => {
    const y = new Date(fechaRelevante(p)).getFullYear() || '—';
    acc[y] = acc[y] || [];
    acc[y].push(p);
    return acc;
  }, {});
  const anios = Object.keys(porAnio).sort((a, b) => Number(b) - Number(a));

  // Resumen
  const resumen = items.reduce((a, p) => {
    a[p.estado] = (a[p.estado] || 0) + 1; return a;
  }, {});

  return (
    <div data-testid="historial-biblioteca-timeline" className="space-y-4">
      {/* Resumen chips */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="text-gray-500">Total: <strong className="text-gray-900">{items.length}</strong></span>
        {Object.entries(resumen).map(([e, n]) => {
          const meta = ESTADO_META[e] || { label: e, color: 'bg-gray-100 text-gray-600 border-gray-300' };
          return (
            <Badge key={e} variant="outline" className={`${meta.color} border`} data-testid={`resumen-${e}`}>
              {meta.label}: {n}
            </Badge>
          );
        })}
      </div>

      {/* Timeline por año */}
      {anios.map(anio => (
        <div key={anio}>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 pl-2">{anio}</div>
          <ol className="relative border-l-2 border-amber-200 ml-2 space-y-3">
            {porAnio[anio].map(p => {
              const meta = ESTADO_META[p.estado] || { color: 'bg-gray-100 text-gray-600', icon: BookOpen, label: p.estado };
              const Icon = meta.icon;
              return (
                <li key={p.id} className="ml-4" data-testid={`historial-item-${p.id}`}>
                  <span className={`absolute -left-[11px] mt-0.5 flex items-center justify-center w-5 h-5 rounded-full ${meta.color} border-2 border-white`}>
                    <Icon className="w-3 h-3" />
                  </span>
                  <div className="rounded-md border bg-white p-3 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm text-gray-900 truncate">
                          {p.titulo?.titulo || 'Título no disponible'}
                        </p>
                        <p className="text-xs text-gray-600 truncate">
                          {p.titulo?.autor || 'Sin autor'}{p.titulo?.editorial ? ` · ${p.titulo.editorial}` : ''}
                        </p>
                        {p.ejemplar?.codigo_ejemplar && (
                          <p className="text-xs text-gray-500 font-mono mt-0.5">Ejemplar: {p.ejemplar.codigo_ejemplar}</p>
                        )}
                      </div>
                      <Badge variant="outline" className={`${meta.color} border shrink-0`}>{meta.label}</Badge>
                    </div>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-xs text-gray-600">
                      {p.fecha_reserva && (
                        <div><span className="text-gray-400">Reservado:</span><br/>{formatearFecha(p.fecha_reserva)}</div>
                      )}
                      {p.fecha_prestamo && (
                        <div><span className="text-gray-400">Retiro:</span><br/>{formatearFecha(p.fecha_prestamo)} <span className="text-gray-400">({p.tipo || '-'})</span></div>
                      )}
                      {p.fecha_vencimiento && (
                        <div><span className="text-gray-400">Vence:</span><br/>{formatearFecha(p.fecha_vencimiento)}</div>
                      )}
                      {p.fecha_devolucion && (
                        <div><span className="text-gray-400">Devuelto:</span><br/>{formatearFecha(p.fecha_devolucion)}</div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </div>
  );
}
